import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from 'react-bootstrap/Button';
import { Dropdown, DropdownBody } from '../../../components/dropdown';
import { ReactComponent as DotsIconSvg } from '../../../shared/icons/dotsRoundIcon.svg';

const DotsIcon = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>((props, ref) => (
  <span {...props} ref={ref} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><DotsIconSvg /></span>
));
import { useDropdownControl } from '../../../contexts/DropdownContext';
import styles from './dealControls.module.scss';
import RenameDealFolderModal from './modals/RenameDealFolderModal';
import MoveDealFolderModal from './modals/MoveDealFolderModal';
import DeleteDealFolderModal from './modals/DeleteDealFolderModal';

type DealFolderControlsProps = {
  folderId: string;
  folderName: string;
  editPermission: boolean;
  managePermission?: boolean;
  teamId: string;
  onSuccess: () => void;
};

type DropdownActionsType = {
  text: string;
  onClick: () => void;
  isAvailable: boolean;
};

enum FolderActionStatus {
  rename = 'Rename',
  move = 'Move',
  delete = 'Delete',
}

const DealFolderControls = ({
  folderId,
  folderName,
  editPermission,
  managePermission,
  teamId,
  onSuccess,
}: DealFolderControlsProps) => {
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dropdownId = useMemo(() => `deal-folder-dropdown-${folderId}`, [folderId]);
  const { isOpen, onToggle } = useDropdownControl(dropdownId);
  const isManageAvailable = managePermission ?? editPermission;

  const allDropDownActions = [
    {
      text: FolderActionStatus.rename,
      onClick: () => {
        setShowRenameModal(true);
      },
      isAvailable: isManageAvailable,
    },
    {
      text: FolderActionStatus.move,
      onClick: () => {
        setShowMoveModal(true);
      },
      isAvailable: isManageAvailable,
    },
    {
      text: FolderActionStatus.delete,
      onClick: () => {
        setShowDeleteModal(true);
      },
      isAvailable: isManageAvailable,
    },
  ];

  const dropDownActions = (actions: DropdownActionsType[]) => {
    return actions
      .filter((action) => action.isAvailable)
      .map((action, index) => (
        <Dropdown.Item as={Button} variant="link" onClick={action.onClick} key={index}>
          {action.text}
        </Dropdown.Item>
      ));
  };

  // Only show dropdown if user has permission for any actions
  const availableActions = allDropDownActions.filter((action) => action.isAvailable);
  const hasAvailableActions = availableActions.length > 0;

  return (
    <>
      {hasAvailableActions && (
        <Dropdown show={isOpen} onToggle={onToggle}>
          <Dropdown.Toggle id="dropdown-deal-folder-actions" className={styles.dottedDropdown} as={DotsIcon}>
            <FormattedMessage id="deals.folder.actions" defaultMessage={`Folder Actions`} />
          </Dropdown.Toggle>
          <Dropdown.Menu align="right">
            <DropdownBody>{dropDownActions(allDropDownActions)}</DropdownBody>
          </Dropdown.Menu>
        </Dropdown>
      )}

      <RenameDealFolderModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        folderId={folderId}
        currentName={folderName}
        teamId={teamId}
        onSuccess={onSuccess}
      />

      <MoveDealFolderModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        folderId={folderId}
        folderName={folderName}
        teamId={teamId}
        onSuccess={onSuccess}
      />

      <DeleteDealFolderModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        folderId={folderId}
        folderName={folderName}
        teamId={teamId}
        onSuccess={onSuccess}
      />
    </>
  );
};

export default DealFolderControls;
